import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import Header from "./components/header/Header";
import Home from "./components/home/Home";
import Services from "./components/services/Services";
import Work from "./components/work/Work";
import CaseFile from "./components/work/CaseFile";
import Research from "./components/research/Research";
import Personalnote from "./components/personalnote/Personalnote";
import Skills from "./components/skills/Skills";
import About from "./components/about/About";
import Content from "./components/content/Content";
import Footer from "./components/footer/Footer";
import ScrollUp from "./components/scrollup/ScrollUp";
import Palette from "./components/palette/Palette";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { nav, profile } from "./data/site";
import { projects } from "./components/work/Data";
import { useTheme } from "./lib/useTheme";
import { useProjectRoute } from "./lib/useProjectRoute";

const projectBySlug = Object.fromEntries(projects.map((p) => [p.slug, p]));

function App() {
  const [theme, toggleTheme] = useTheme();
  const { slug, open: openProject, close: closeProject } = useProjectRoute();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // On load or refresh, start at the top instead of wherever the browser last was.
  // A section link (#skills) still jumps to that section; case-file links (#/work/...) are left alone.
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const id = window.location.hash.slice(1);
    const target = id && !id.startsWith("/") ? document.getElementById(id) : null;
    if (target) target.scrollIntoView();
    else if (!id) window.scrollTo(0, 0);
  }, []);

  // Global shortcut: Cmd/Ctrl + K toggles the command palette.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const commands = useMemo(
    () => [
      ...nav.map((item) => ({
        id: `nav-${item.id}`,
        group: "Go to",
        label: item.label,
        run: () => {
          window.location.hash = item.id;
        },
      })),
      ...projects.map((p) => ({
        id: `project-${p.slug}`,
        group: "Case files",
        label: p.title,
        hint: p.status,
        keywords: `${p.stack.join(" ")} ${p.domain}`,
        run: () => openProject(p.slug),
      })),
      { id: "link-github", group: "Links", label: "GitHub", hint: "DEADSERPENT", run: () => window.open(profile.github, "_blank", "noopener") },
      { id: "link-scholar", group: "Links", label: "Google Scholar", hint: "Publications", run: () => window.open(profile.scholar, "_blank", "noopener") },
      { id: "link-orcid", group: "Links", label: "ORCID", hint: "0009-0007-9780-5794", run: () => window.open(profile.orcid, "_blank", "noopener") },
      { id: "link-figma", group: "Links", label: "Figma", hint: "Design files", run: () => window.open(profile.figma, "_blank", "noopener") },
      { id: "link-telegram", group: "Links", label: "Telegram", hint: "@SAMARTHASMG", run: () => window.open(profile.telegram, "_blank", "noopener") },
      { id: "link-linkedin", group: "Links", label: "LinkedIn", run: () => window.open(profile.linkedin, "_blank", "noopener") },
      { id: "link-mail", group: "Links", label: "Email", hint: profile.email, run: () => (window.location.href = `mailto:${profile.email}`) },
      { id: "act-theme", group: "Actions", label: `Switch to ${theme === "dark" ? "light" : "dark"} theme`, run: toggleTheme },
    ],
    [openProject, theme, toggleTheme]
  );

  const activeProject = slug ? projectBySlug[slug] : null;

  return (
    <>
      <a className="skip-link" href="#work">
        Skip to projects
      </a>
      <Header theme={theme} onToggleTheme={toggleTheme} onOpenPalette={() => setPaletteOpen(true)} />

      <main id="main">
        <Home onOpenProject={openProject} />
        <Services />
        <Work onOpenProject={openProject} />
        <Research onOpenProject={openProject} />
        <Personalnote onOpenProject={openProject} />
        <Skills />
        <About />
        <Content />
      </main>

      <Footer />
      <ScrollUp />
      <Palette open={paletteOpen} onClose={() => setPaletteOpen(false)} commands={commands} />
      {activeProject && <CaseFile key={activeProject.slug} project={activeProject} onClose={closeProject} />}
      <SpeedInsights />
    </>
  );
}

export default App;
